
import { computed, defineComponent, inject, ref } from "vue";
import ViewHeader from "@/components/ViewHeader.vue";
import BackAndCancelFooter from "@/components/BackAndCancelFooter.vue";
import TextAreaWithClear from "@/components/TextAreaWithClear.vue";
import Checkbox from "primevue/checkbox";
import { useStore } from "@/store";
import { useRouter } from "vue-router";
import swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import TextInput from "@/components/Inputs/TextInput.vue";
import DropdownInput from "@/components/Inputs/DropdownInput.vue";
import useVuelidate, { ValidationArgs, ValidationRule } from "@vuelidate/core";
import { email } from "@vuelidate/validators";
import { Validation } from "@/models/Validation";
import { api } from "@/services/Api";
import q from "q";
import Q from "q";
import { ValidateOrderResponse } from "@/models/Interfaces";
import { ApiResult } from "@/services/ApiResult";
import { VIRTUAL_SLOT_ID } from '@/models/Constants';

export default defineComponent({
  props: {
    isGroup: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ViewHeader,
    BackAndCancelFooter,
    TextAreaWithClear,
    DropdownInput,
    TextInput,
    Checkbox,
  },
  async setup(props) {
    const sendEmailSms = ref(false);
    const store = useStore();
    const router = useRouter();
    const { t } = useI18n();
    const isSave = computed(() => router.currentRoute.value.meta?.save);
    const invalidMobile = ref(false);
    const orderSourceOptions = computed(() => store.state.orderSources);
    const emitter: any = inject('emitter');

    const customer = computed(() => store.state.order.customer);

    async function pay() {
      const orderValidation = store.state.orderValidation;

      const validated = await Promise.race([orderValidation, Promise.resolve(-1)]);

      let validationResult: ApiResult<ValidateOrderResponse> | null;

      if (validated == VIRTUAL_SLOT_ID) {
        swal.showLoading();
        const delay = Q.delay(400);
        await delay;
      }

      validationResult = await orderValidation;

      if (validationResult?.errorMessage) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: validationResult.errorMessage,
        });
        return;
      }
      if (!validationResult?.data) {
        swal.close();
        return;
      }
      if (validationResult.data.notAvailableProducts?.length > 0 || (validationResult.data.voucherResult && validationResult.data.voucherResult.resultId != 0)) {
        emitter.emit('order-validation-error');
        router.back();
        return;
      }
      swal.close();
      router.push({ name: "selectPayment" });
    }

    const hasAnyFieldFilled = computed(() => {
      const { firstName, lastName, mobile, email, groupName } = customer.value;
      return (
        (firstName && firstName.trim()) ||
        (lastName && lastName.trim()) ||
        (mobile && mobile.trim()) ||
        (email && email.trim()) ||
        (props.isGroup && groupName && groupName.trim())
      );
    });

    // Required only if no fields are filled
    const requiredUntilOneFilled: ValidationRule = (value: string | undefined): boolean => {
      if (hasAnyFieldFilled.value) {
        
        // Validate if field has value
        return !value || (typeof value === 'string' && value.trim().length > 0);
      }

      // If no fields are filled, all fields required
      return typeof value === 'string' && value.trim().length > 0;
    };

    const submitForm = async (event: { preventDefault: () => void; stopPropagation: () => void; }) => {
      invalidMobile.value = false;

      const valid = await v.value.$validate();
      if (!valid) {
        event.preventDefault();
        event.stopPropagation();
        swal.fire({
          icon: "info",
          text: t("alert.pleaseFillAllRequiredFields"),
        });
      } else {
        if (!isSave.value) {
          pay();
        } else {
          const response = await SaveCustomerDetails();
          if (response.errorMessage) {
            if (response.errorMessage == "Invalid mobile") {
              invalidMobile.value = true;
            }
            await swal.fire({
              icon: "error",
              title: t("error-pop-up.oops"),
              text: response.errorMessage,
            });
            return;
          }

          const orderNumber = store.state.lang == "he-IL" ? ` ${store.state.order.orderNumber}# ` : ` #${store.state.order.orderNumber} `

          await swal.fire({
            icon: "success",
            text: t("cashier.alert.order-saved") + orderNumber,
            confirmButtonText: t("cashier.label.ok"),
          });
          
          window.location.reload();
        }
      }
    };

    async function SaveCustomerDetails() {
      swal.showLoading();
      var responsePromise = api.SaveCustomerDetails(
        store.state.order.id,
        store.state.order.customer
      );
      await q.delay(400);
      return await responsePromise;
    }

    const rules: ValidationArgs = {
      firstName: { requiredUntilOneFilled },
      lastName: { requiredUntilOneFilled },
      mobile: { requiredUntilOneFilled },
      email: { requiredUntilOneFilled, email }, // Email must also be valid if filled
      pnr: {},
      groupName: { requiredUntilOneFilled: props.isGroup ? requiredUntilOneFilled : (): boolean => true }, // No validation if not a group
      sourceOptionId: {},
    };

    const scope = "validationScope";
    const validation = new Validation(rules, scope);

    const v = useVuelidate({ $scope: scope });

    return {
      sendEmailSms,
      customer,
      pay,
      submitForm,
      validation,
      v,
      isSave,
      invalidMobile,
      orderSourceOptions,
    };
  },
});
